import { useEffect } from 'react';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getMe, type MeResponse } from '../api/user';
import { provisionUser } from '../api/account';
import { useAuthStore } from '../stores/authStore';
import type { UserDoc } from '../types/domain';
import { isProfileComplete } from '../utils/profileValidation';
import { clearBackendHealthCache } from '../utils/backendHealth';
import { authDebug } from '../lib/auth-debug';

let authListenerAttached = false;
let authBootstrapGeneration = 0;

function mapMeToUserDoc(me: MeResponse): UserDoc {
  const customerCompleted = isProfileComplete({
    firstName: me.firstName,
    lastName: me.lastName,
    documentNumber: me.documentNumber,
    phone: me.phone,
  });

  authDebug('Resposta /accounts/me mapeada', {
    email: me.email,
    firebaseUid: me.firebaseUid,
    isGlobalAdmin: me.isGlobalAdmin,
    customerCompleted,
  });

  return {
    uid: me.firebaseUid,
    email: me.email,
    displayName:
      me.firstName && me.lastName ? `${me.firstName} ${me.lastName}` : undefined,
    photoURL: me.photoUrl,
    customerCompleted,
    customer:
      me.firstName || me.lastName || me.documentNumber || me.phone
        ? {
            firstName: me.firstName ?? '',
            lastName: me.lastName ?? '',
            documentNumber: me.documentNumber ?? '',
            phone: me.phone,
          }
        : undefined,
    firstName: me.firstName,
    lastName: me.lastName,
    lastSelectedTenantSlug: me.lastSelectedTenantSlug,
    isGlobalAdmin: me.isGlobalAdmin ?? false,
    isCondominiumManager: me.isCondominiumManager ?? false,
    createdAt: new Date(me.createdAt),
    updatedAt: new Date(me.updatedAt),
  };
}

async function fetchUserDoc(): Promise<UserDoc> {
  let me: MeResponse;
  try {
    me = await getMe();
  } catch (error: unknown) {
    const status = (error as { status?: number })?.status;
    if (status === 404) {
      me = await provisionUser();
    } else {
      throw error;
    }
  }
  return mapMeToUserDoc(me);
}

async function syncAuthenticatedUser(user: User, generation: number): Promise<void> {
  const { setAuthUser, setUserDoc, setLoading, clearAuth } = useAuthStore.getState();
  const isStale = () => generation !== authBootstrapGeneration;

  setLoading(true);
  setAuthUser(user);

  try {
    await auth.authStateReady();
    if (isStale()) return;

    await user.getIdToken();
    if (isStale()) return;

    const doc = await fetchUserDoc();
    if (isStale()) return;
    authDebug('userDoc salvo no store', {
      email: doc.email,
      uid: doc.uid,
      isGlobalAdmin: doc.isGlobalAdmin,
      customerCompleted: doc.customerCompleted,
    });
    setUserDoc(doc);
  } catch (error: unknown) {
    if (isStale()) return;

    const status = (error as { status?: number })?.status;
    if (status === 401) {
      clearAuth();
      clearBackendHealthCache();
      await signOut(auth).catch(() => undefined);
      return;
    }

    setUserDoc(null);
  } finally {
    if (!isStale()) {
      setLoading(false);
    }
  }
}

export async function refreshUserDoc(): Promise<UserDoc | null> {
  try {
    await auth.authStateReady();
    if (!auth.currentUser) return null;
    const doc = await fetchUserDoc();
    useAuthStore.getState().setUserDoc(doc);
    return doc;
  } catch {
    return null;
  }
}

/** Atualiza o store a partir de uma resposta `/accounts/me` (ex.: PUT /accounts/me). */
export function applyMeResponseToStore(me: MeResponse): UserDoc {
  const doc = mapMeToUserDoc(me);
  useAuthStore.getState().setUserDoc(doc);
  return doc;
}

export function useAuthBootstrap() {
  useEffect(() => {
    if (authListenerAttached) {
      void (async () => {
        await auth.authStateReady();
        const user = auth.currentUser;
        const { userDoc, loading } = useAuthStore.getState();
        if (user && !userDoc && !loading) {
          const generation = ++authBootstrapGeneration;
          await syncAuthenticatedUser(user, generation);
        }
      })();
      return;
    }

    authListenerAttached = true;

    onAuthStateChanged(auth, (user) => {
      const generation = ++authBootstrapGeneration;
      const { clearAuth } = useAuthStore.getState();

      if (!user) {
        // Cold start (PWA/mobile): Firebase pode emitir null antes de restaurar
        // a sessão persistida. Só trata como deslogado após authStateReady().
        void (async () => {
          await auth.authStateReady();
          if (generation !== authBootstrapGeneration) return;

          const currentUser = auth.currentUser;
          if (currentUser) {
            await syncAuthenticatedUser(currentUser, generation);
            return;
          }

          clearAuth();
          clearBackendHealthCache();
        })();
        return;
      }

      void syncAuthenticatedUser(user, generation);
    });

    // Mantém o listener ativo (evita race no StrictMode / refresh).
  }, []);
}

export function useAuth() {
  return useAuthStore();
}
